/**
 * Role-Based Access Control (RBAC) Configuration
 * Defines all roles, permissions, and navigation access
 * 
 * PRINCIPLES:
 * - Default-deny: nothing visible unless explicitly allowed
 * - Access scoped by hierarchy: Brand → Country → Franchise → Outlet
 * - Enforced at: Navigation, Route, Action, and Data query levels
 */

import {
  LayoutDashboard, ShoppingCart, Package, TrendingUp, Receipt, CheckSquare,
  Users, CalendarDays, UserCircle, FileText, FileCheck, BarChart3, Brain,
  Settings, ClipboardList, Bell, Globe, Building2, MapPin, ChefHat, Utensils,
  Upload, ShoppingBag, Heart, History, Store
} from 'lucide-react';

// ============================================
// ROLE DEFINITIONS
// ============================================

export const ROLES = {
  BRAND_OWNER: 'brand_owner',
  CENTRAL_ADMIN: 'central_admin',
  FRANCHISE_OWNER: 'franchise_owner',
  REGIONAL_MANAGER: 'regional_manager',
  RESTAURANT_MANAGER: 'restaurant_manager',
  CHEF: 'chef',
  FOH_STAFF: 'foh_staff',
  SUPPLIER: 'supplier',
  CUSTOMER: 'customer',
};

export const ROLE_DISPLAY_NAMES = {
  [ROLES.BRAND_OWNER]: 'Brand Owner / Group Owner',
  [ROLES.CENTRAL_ADMIN]: 'Central Admin',
  [ROLES.FRANCHISE_OWNER]: 'Franchise Owner',
  [ROLES.REGIONAL_MANAGER]: 'Regional Manager',
  [ROLES.RESTAURANT_MANAGER]: 'Restaurant Manager',
  [ROLES.CHEF]: 'Chef / Kitchen Staff',
  [ROLES.FOH_STAFF]: 'Front-of-House Staff',
  [ROLES.SUPPLIER]: 'Supplier / Vendor',
  [ROLES.CUSTOMER]: 'Customer',
};

export const ROLE_DESCRIPTIONS = {
  [ROLES.BRAND_OWNER]: 'Full visibility across all franchises, outlets, and financial reports',
  [ROLES.CENTRAL_ADMIN]: 'System configuration, user management, and role administration',
  [ROLES.FRANCHISE_OWNER]: 'Oversight of franchise outlets, KPIs, and financial summaries',
  [ROLES.REGIONAL_MANAGER]: 'Regional dashboard, outlet comparison, and staff analytics',
  [ROLES.RESTAURANT_MANAGER]: 'Full outlet operations: POS, inventory, staff, and reporting',
  [ROLES.CHEF]: 'Kitchen queue, order preparation, and limited inventory access',
  [ROLES.FOH_STAFF]: 'POS operations, tables, walk-ins, and payments',
  [ROLES.SUPPLIER]: 'Purchase orders and invoice/document upload',
  [ROLES.CUSTOMER]: 'Online ordering, reservations, loyalty, and order history',
};

// Role hierarchy level (lower = more powerful)
export const ROLE_LEVELS = {
  [ROLES.BRAND_OWNER]: 1,
  [ROLES.CENTRAL_ADMIN]: 2,
  [ROLES.FRANCHISE_OWNER]: 3,
  [ROLES.REGIONAL_MANAGER]: 4,
  [ROLES.RESTAURANT_MANAGER]: 5,
  [ROLES.CHEF]: 6,
  [ROLES.FOH_STAFF]: 6,
  [ROLES.SUPPLIER]: 7,
  [ROLES.CUSTOMER]: 8,
};

// ============================================
// PERMISSION DEFINITIONS
// ============================================

export const PERMISSIONS = {
  // Dashboard & Overview
  VIEW_GLOBAL_DASHBOARD: 'view_global_dashboard',
  VIEW_FRANCHISE_DASHBOARD: 'view_franchise_dashboard',
  VIEW_REGIONAL_DASHBOARD: 'view_regional_dashboard',
  VIEW_OUTLET_DASHBOARD: 'view_outlet_dashboard',
  
  // POS & Orders
  ACCESS_POS: 'access_pos',
  CREATE_ORDERS: 'create_orders',
  VOID_ORDERS: 'void_orders',
  APPLY_DISCOUNTS: 'apply_discounts',
  
  // Kitchen
  VIEW_KITCHEN_QUEUE: 'view_kitchen_queue',
  UPDATE_ORDER_STATUS: 'update_order_status',
  
  // Tables & Reservations
  MANAGE_TABLES: 'manage_tables',
  VIEW_RESERVATIONS: 'view_reservations',
  CREATE_RESERVATIONS: 'create_reservations',
  MANAGE_RESERVATIONS: 'manage_reservations',
  
  // Inventory
  VIEW_INVENTORY: 'view_inventory',
  MANAGE_INVENTORY: 'manage_inventory',
  CREATE_PURCHASE_ORDERS: 'create_purchase_orders',
  
  // Finance
  VIEW_FINANCE: 'view_finance',
  VIEW_FINANCIAL_REPORTS: 'view_financial_reports',
  MANAGE_EXPENSES: 'manage_expenses',
  APPROVE_EXPENSES: 'approve_expenses',
  
  // Staff & HR
  VIEW_STAFF: 'view_staff',
  MANAGE_STAFF: 'manage_staff',
  VIEW_STAFF_ANALYTICS: 'view_staff_analytics',
  
  // Customers & Loyalty
  VIEW_CUSTOMERS: 'view_customers',
  MANAGE_CUSTOMERS: 'manage_customers',
  MANAGE_LOYALTY: 'manage_loyalty',
  
  // Compliance & Documents
  VIEW_DOCUMENTS: 'view_documents',
  UPLOAD_DOCUMENTS: 'upload_documents',
  MANAGE_LICENSES: 'manage_licenses',
  
  // Analytics & AI
  VIEW_ANALYTICS: 'view_analytics',
  VIEW_AI_ANALYTICS: 'view_ai_analytics',
  
  // Admin & System
  ACCESS_ADMIN: 'access_admin',
  MANAGE_USERS: 'manage_users',
  MANAGE_ROLES: 'manage_roles',
  VIEW_AUDIT_LOG: 'view_audit_log',
  MANAGE_INTEGRATIONS: 'manage_integrations',
  MANAGE_FEATURE_FLAGS: 'manage_feature_flags',
  MANAGE_WORKFLOWS: 'manage_workflows',
  
  // Approvals
  VIEW_APPROVALS: 'view_approvals',
  APPROVE_ITEMS: 'approve_items',
  
  // Supplier Portal
  VIEW_PURCHASE_ORDERS: 'view_purchase_orders',
  SUBMIT_INVOICES: 'submit_invoices',
  
  // Customer Portal
  ONLINE_ORDERING: 'online_ordering',
  VIEW_ORDER_HISTORY: 'view_order_history',
  MANAGE_OWN_RESERVATIONS: 'manage_own_reservations',
  VIEW_LOYALTY_POINTS: 'view_loyalty_points',
};

// ============================================
// ROLE → PERMISSIONS MAPPING
// ============================================

export const ROLE_PERMISSIONS = {
  [ROLES.BRAND_OWNER]: [
    // Full access to all dashboards
    PERMISSIONS.VIEW_GLOBAL_DASHBOARD,
    PERMISSIONS.VIEW_FRANCHISE_DASHBOARD,
    PERMISSIONS.VIEW_REGIONAL_DASHBOARD,
    PERMISSIONS.VIEW_OUTLET_DASHBOARD,
    // POS & Operations
    PERMISSIONS.ACCESS_POS,
    PERMISSIONS.CREATE_ORDERS,
    PERMISSIONS.VOID_ORDERS,
    PERMISSIONS.APPLY_DISCOUNTS,
    PERMISSIONS.VIEW_KITCHEN_QUEUE,
    PERMISSIONS.UPDATE_ORDER_STATUS,
    PERMISSIONS.MANAGE_TABLES,
    // Reservations
    PERMISSIONS.VIEW_RESERVATIONS,
    PERMISSIONS.CREATE_RESERVATIONS,
    PERMISSIONS.MANAGE_RESERVATIONS,
    // Inventory
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.MANAGE_INVENTORY,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
    // Finance
    PERMISSIONS.VIEW_FINANCE,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.APPROVE_EXPENSES,
    // Staff & HR
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.VIEW_STAFF_ANALYTICS,
    // Customers
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.MANAGE_LOYALTY,
    // Documents
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.UPLOAD_DOCUMENTS,
    PERMISSIONS.MANAGE_LICENSES,
    // Analytics
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    // Admin & System
    PERMISSIONS.ACCESS_ADMIN,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_ROLES,
    PERMISSIONS.VIEW_AUDIT_LOG,
    PERMISSIONS.MANAGE_INTEGRATIONS,
    PERMISSIONS.MANAGE_FEATURE_FLAGS,
    PERMISSIONS.MANAGE_WORKFLOWS,
    // Approvals
    PERMISSIONS.VIEW_APPROVALS,
    PERMISSIONS.APPROVE_ITEMS,
  ],
  
  [ROLES.CENTRAL_ADMIN]: [
    // Full access to all dashboards
    PERMISSIONS.VIEW_GLOBAL_DASHBOARD,
    PERMISSIONS.VIEW_FRANCHISE_DASHBOARD,
    PERMISSIONS.VIEW_REGIONAL_DASHBOARD,
    PERMISSIONS.VIEW_OUTLET_DASHBOARD,
    // POS & Operations
    PERMISSIONS.ACCESS_POS,
    PERMISSIONS.CREATE_ORDERS,
    PERMISSIONS.VOID_ORDERS,
    PERMISSIONS.APPLY_DISCOUNTS,
    PERMISSIONS.VIEW_KITCHEN_QUEUE,
    PERMISSIONS.UPDATE_ORDER_STATUS,
    PERMISSIONS.MANAGE_TABLES,
    // Reservations
    PERMISSIONS.VIEW_RESERVATIONS,
    PERMISSIONS.CREATE_RESERVATIONS,
    PERMISSIONS.MANAGE_RESERVATIONS,
    // Inventory
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.MANAGE_INVENTORY,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
    // Finance
    PERMISSIONS.VIEW_FINANCE,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.APPROVE_EXPENSES,
    // Staff & HR
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.VIEW_STAFF_ANALYTICS,
    // Customers
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.MANAGE_LOYALTY,
    // Documents
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.UPLOAD_DOCUMENTS,
    PERMISSIONS.MANAGE_LICENSES,
    // Analytics
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    // Admin & System
    PERMISSIONS.ACCESS_ADMIN,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.MANAGE_ROLES,
    PERMISSIONS.VIEW_AUDIT_LOG,
    PERMISSIONS.MANAGE_INTEGRATIONS,
    PERMISSIONS.MANAGE_FEATURE_FLAGS,
    PERMISSIONS.MANAGE_WORKFLOWS,
    // Approvals
    PERMISSIONS.VIEW_APPROVALS,
    PERMISSIONS.APPROVE_ITEMS,
  ],
  
  [ROLES.FRANCHISE_OWNER]: [
    PERMISSIONS.VIEW_FRANCHISE_DASHBOARD,
    PERMISSIONS.VIEW_OUTLET_DASHBOARD,
    PERMISSIONS.VIEW_FINANCIAL_REPORTS,
    PERMISSIONS.VIEW_FINANCE,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.VIEW_STAFF_ANALYTICS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_APPROVALS,
    PERMISSIONS.APPROVE_ITEMS,
    PERMISSIONS.APPROVE_EXPENSES,
  ],
  
  [ROLES.REGIONAL_MANAGER]: [
    PERMISSIONS.VIEW_REGIONAL_DASHBOARD,
    PERMISSIONS.VIEW_OUTLET_DASHBOARD,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.VIEW_STAFF_ANALYTICS,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.VIEW_APPROVALS,
  ],
  
  [ROLES.RESTAURANT_MANAGER]: [
    PERMISSIONS.VIEW_OUTLET_DASHBOARD,
    PERMISSIONS.ACCESS_POS,
    PERMISSIONS.CREATE_ORDERS,
    PERMISSIONS.VOID_ORDERS,
    PERMISSIONS.APPLY_DISCOUNTS,
    PERMISSIONS.MANAGE_TABLES,
    PERMISSIONS.VIEW_RESERVATIONS,
    PERMISSIONS.CREATE_RESERVATIONS,
    PERMISSIONS.MANAGE_RESERVATIONS,
    PERMISSIONS.VIEW_INVENTORY,
    PERMISSIONS.MANAGE_INVENTORY,
    PERMISSIONS.CREATE_PURCHASE_ORDERS,
    PERMISSIONS.VIEW_FINANCE,
    PERMISSIONS.MANAGE_EXPENSES,
    PERMISSIONS.VIEW_STAFF,
    PERMISSIONS.MANAGE_STAFF,
    PERMISSIONS.VIEW_CUSTOMERS,
    PERMISSIONS.MANAGE_CUSTOMERS,
    PERMISSIONS.VIEW_DOCUMENTS,
    PERMISSIONS.MANAGE_LICENSES,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_AI_ANALYTICS,
    PERMISSIONS.VIEW_APPROVALS,
    PERMISSIONS.APPROVE_ITEMS,
  ],
  
  [ROLES.CHEF]: [
    PERMISSIONS.VIEW_KITCHEN_QUEUE,
    PERMISSIONS.UPDATE_ORDER_STATUS,
    PERMISSIONS.VIEW_INVENTORY,
  ],
  
  [ROLES.FOH_STAFF]: [
    PERMISSIONS.ACCESS_POS,
    PERMISSIONS.CREATE_ORDERS,
    PERMISSIONS.MANAGE_TABLES,
    PERMISSIONS.VIEW_RESERVATIONS,
    PERMISSIONS.CREATE_RESERVATIONS,
  ],
  
  [ROLES.SUPPLIER]: [
    PERMISSIONS.VIEW_PURCHASE_ORDERS,
    PERMISSIONS.SUBMIT_INVOICES,
    PERMISSIONS.UPLOAD_DOCUMENTS,
  ],
  
  [ROLES.CUSTOMER]: [
    PERMISSIONS.ONLINE_ORDERING,
    PERMISSIONS.VIEW_ORDER_HISTORY,
    PERMISSIONS.MANAGE_OWN_RESERVATIONS,
    PERMISSIONS.VIEW_LOYALTY_POINTS,
  ],
};

// ============================================
// NAVIGATION CONFIGURATION
// ============================================

export const NAVIGATION_CONFIG = {
  // Dashboard variations based on role
  dashboards: {
    global: { name: 'Global Dashboard', href: '/dashboard', icon: Globe, permission: PERMISSIONS.VIEW_GLOBAL_DASHBOARD },
    franchise: { name: 'Franchise Dashboard', href: '/dashboard', icon: Building2, permission: PERMISSIONS.VIEW_FRANCHISE_DASHBOARD },
    regional: { name: 'Regional Dashboard', href: '/dashboard', icon: MapPin, permission: PERMISSIONS.VIEW_REGIONAL_DASHBOARD },
    outlet: { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: PERMISSIONS.VIEW_OUTLET_DASHBOARD },
  },
  
  // All navigation items with permissions
  items: [
    // Operations
    { name: 'POS', href: '/pos', icon: ShoppingCart, section: 'OPERATIONS', permission: PERMISSIONS.ACCESS_POS, testId: 'nav-pos' },
    { name: 'Kitchen Queue', href: '/kitchen', icon: ChefHat, section: 'OPERATIONS', permission: PERMISSIONS.VIEW_KITCHEN_QUEUE, testId: 'nav-kitchen' },
    { name: 'Tables', href: '/tables', icon: Utensils, section: 'OPERATIONS', permission: PERMISSIONS.MANAGE_TABLES, testId: 'nav-tables' },
    { name: 'Inventory', href: '/inventory', icon: Package, section: 'OPERATIONS', permission: PERMISSIONS.VIEW_INVENTORY, testId: 'nav-inventory' },
    { name: 'Finance', href: '/finance', icon: TrendingUp, section: 'OPERATIONS', permission: PERMISSIONS.VIEW_FINANCE, testId: 'nav-finance' },
    { name: 'Expenses', href: '/expenses', icon: Receipt, section: 'OPERATIONS', permission: PERMISSIONS.MANAGE_EXPENSES, testId: 'nav-expenses' },
    
    // Management
    { name: 'Approvals', href: '/approvals', icon: CheckSquare, section: 'MANAGEMENT', permission: PERMISSIONS.VIEW_APPROVALS, badge: true, testId: 'nav-approvals' },
    { name: 'HR & Staff', href: '/hr', icon: Users, section: 'MANAGEMENT', permission: PERMISSIONS.VIEW_STAFF, testId: 'nav-hr' },
    { name: 'Reservations', href: '/reservations', icon: CalendarDays, section: 'MANAGEMENT', permission: PERMISSIONS.VIEW_RESERVATIONS, testId: 'nav-reservations' },
    { name: 'Customers', href: '/customers', icon: UserCircle, section: 'MANAGEMENT', permission: PERMISSIONS.VIEW_CUSTOMERS, testId: 'nav-customers' },
    
    // Compliance
    { name: 'Documents', href: '/documents', icon: FileText, section: 'COMPLIANCE', permission: PERMISSIONS.VIEW_DOCUMENTS, testId: 'nav-documents' },
    { name: 'Licenses', href: '/licenses', icon: FileCheck, section: 'COMPLIANCE', permission: PERMISSIONS.MANAGE_LICENSES, testId: 'nav-licenses' },
    
    // Insights
    { name: 'Analytics', href: '/analytics', icon: BarChart3, section: 'INSIGHTS', permission: PERMISSIONS.VIEW_ANALYTICS, testId: 'nav-analytics' },
    { name: 'AI Analytics', href: '/ai-analytics', icon: Brain, section: 'INSIGHTS', permission: PERMISSIONS.VIEW_AI_ANALYTICS, testId: 'nav-ai-analytics' },
    
    // System (Admin only)
    { name: 'Admin', href: '/admin', icon: Settings, section: 'SYSTEM', permission: PERMISSIONS.ACCESS_ADMIN, testId: 'nav-admin' },
    { name: 'User Management', href: '/admin/users', icon: Users, section: 'SYSTEM', permission: PERMISSIONS.MANAGE_USERS, testId: 'nav-users' },
    { name: 'Workflows', href: '/workflows', icon: ClipboardList, section: 'SYSTEM', permission: PERMISSIONS.MANAGE_WORKFLOWS, testId: 'nav-workflows' },
    { name: 'Audit Log', href: '/audit', icon: FileCheck, section: 'SYSTEM', permission: PERMISSIONS.VIEW_AUDIT_LOG, testId: 'nav-audit' },
    { name: 'Notifications', href: '/notifications', icon: Bell, section: 'SYSTEM', permission: null, testId: 'nav-notifications' }, // All roles
    
    // Supplier Portal
    { name: 'Purchase Orders', href: '/supplier/orders', icon: ShoppingBag, section: 'SUPPLIER_PORTAL', permission: PERMISSIONS.VIEW_PURCHASE_ORDERS, testId: 'nav-supplier-orders' },
    { name: 'Upload Documents', href: '/supplier/upload', icon: Upload, section: 'SUPPLIER_PORTAL', permission: PERMISSIONS.SUBMIT_INVOICES, testId: 'nav-supplier-upload' },
    
    // Customer Portal
    { name: 'Order Online', href: '/order', icon: Store, section: 'CUSTOMER_PORTAL', permission: PERMISSIONS.ONLINE_ORDERING, testId: 'nav-customer-order' },
    { name: 'My Reservations', href: '/my-reservations', icon: CalendarDays, section: 'CUSTOMER_PORTAL', permission: PERMISSIONS.MANAGE_OWN_RESERVATIONS, testId: 'nav-customer-reservations' },
    { name: 'Loyalty & Rewards', href: '/loyalty', icon: Heart, section: 'CUSTOMER_PORTAL', permission: PERMISSIONS.VIEW_LOYALTY_POINTS, testId: 'nav-customer-loyalty' },
    { name: 'Order History', href: '/order-history', icon: History, section: 'CUSTOMER_PORTAL', permission: PERMISSIONS.VIEW_ORDER_HISTORY, testId: 'nav-customer-history' },
  ],
};

// ============================================
// SECTION DISPLAY CONFIGURATION
// ============================================

export const SECTION_CONFIG = {
  OPERATIONS: { name: 'OPERATIONS', order: 1 },
  MANAGEMENT: { name: 'MANAGEMENT', order: 2 },
  COMPLIANCE: { name: 'COMPLIANCE', order: 3 },
  INSIGHTS: { name: 'INSIGHTS', order: 4 },
  SYSTEM: { name: 'SYSTEM', order: 5 },
  SUPPLIER_PORTAL: { name: 'SUPPLIER PORTAL', order: 6 },
  CUSTOMER_PORTAL: { name: 'MY ACCOUNT', order: 7 },
};

// ============================================
// ROLE → VISIBLE SECTIONS
// ============================================

export const ROLE_SECTIONS = {
  [ROLES.BRAND_OWNER]: ['OPERATIONS', 'MANAGEMENT', 'COMPLIANCE', 'INSIGHTS', 'SYSTEM'],
  [ROLES.CENTRAL_ADMIN]: ['OPERATIONS', 'MANAGEMENT', 'COMPLIANCE', 'INSIGHTS', 'SYSTEM'],
  [ROLES.FRANCHISE_OWNER]: ['OPERATIONS', 'MANAGEMENT', 'INSIGHTS'],
  [ROLES.REGIONAL_MANAGER]: ['OPERATIONS', 'MANAGEMENT', 'INSIGHTS'],
  [ROLES.RESTAURANT_MANAGER]: ['OPERATIONS', 'MANAGEMENT', 'COMPLIANCE', 'INSIGHTS'],
  [ROLES.CHEF]: ['OPERATIONS'],
  [ROLES.FOH_STAFF]: ['OPERATIONS'],
  [ROLES.SUPPLIER]: ['SUPPLIER_PORTAL'],
  [ROLES.CUSTOMER]: ['CUSTOMER_PORTAL'],
};

// ============================================
// ROUTE PROTECTION CONFIG
// ============================================

export const ROUTE_PERMISSIONS = {
  '/dashboard': [PERMISSIONS.VIEW_GLOBAL_DASHBOARD, PERMISSIONS.VIEW_FRANCHISE_DASHBOARD, PERMISSIONS.VIEW_REGIONAL_DASHBOARD, PERMISSIONS.VIEW_OUTLET_DASHBOARD],
  '/pos': [PERMISSIONS.ACCESS_POS],
  '/kitchen': [PERMISSIONS.VIEW_KITCHEN_QUEUE],
  '/tables': [PERMISSIONS.MANAGE_TABLES],
  '/inventory': [PERMISSIONS.VIEW_INVENTORY],
  '/finance': [PERMISSIONS.VIEW_FINANCE, PERMISSIONS.VIEW_FINANCIAL_REPORTS],
  '/expenses': [PERMISSIONS.MANAGE_EXPENSES],
  '/approvals': [PERMISSIONS.VIEW_APPROVALS],
  '/hr': [PERMISSIONS.VIEW_STAFF],
  '/reservations': [PERMISSIONS.VIEW_RESERVATIONS],
  '/customers': [PERMISSIONS.VIEW_CUSTOMERS],
  '/documents': [PERMISSIONS.VIEW_DOCUMENTS],
  '/licenses': [PERMISSIONS.MANAGE_LICENSES],
  '/analytics': [PERMISSIONS.VIEW_ANALYTICS],
  '/ai-analytics': [PERMISSIONS.VIEW_AI_ANALYTICS],
  '/admin': [PERMISSIONS.ACCESS_ADMIN],
  '/admin/users': [PERMISSIONS.MANAGE_USERS],
  '/workflows': [PERMISSIONS.MANAGE_WORKFLOWS],
  '/audit': [PERMISSIONS.VIEW_AUDIT_LOG],
  '/notifications': null, // All authenticated users
  // Supplier portal
  '/supplier/orders': [PERMISSIONS.VIEW_PURCHASE_ORDERS],
  '/supplier/upload': [PERMISSIONS.SUBMIT_INVOICES],
  // Customer portal
  '/order': [PERMISSIONS.ONLINE_ORDERING],
  '/my-reservations': [PERMISSIONS.MANAGE_OWN_RESERVATIONS],
  '/loyalty': [PERMISSIONS.VIEW_LOYALTY_POINTS],
  '/order-history': [PERMISSIONS.VIEW_ORDER_HISTORY],
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role, permission) {
  if (!role || !permission) return false;
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  return rolePermissions.includes(permission);
}

/**
 * Check if a role has any of the specified permissions
 */
export function hasAnyPermission(role, permissions) {
  if (!role || !permissions || permissions.length === 0) return true; // No permissions required
  return permissions.some(p => hasPermission(role, p));
}

/**
 * Check if a role can access a route
 */
export function canAccessRoute(role, path) {
  const requiredPermissions = ROUTE_PERMISSIONS[path];
  if (requiredPermissions === null || requiredPermissions === undefined) return true;
  return hasAnyPermission(role, requiredPermissions);
}

/**
 * Get navigation items filtered by role
 */
export function getNavigationForRole(role) {
  if (!role) return [];
  
  const allowedSections = ROLE_SECTIONS[role] || [];
  const rolePermissions = ROLE_PERMISSIONS[role] || [];
  
  // Filter items by section and permission
  const filteredItems = NAVIGATION_CONFIG.items.filter(item => {
    // Check if section is allowed for this role
    if (!allowedSections.includes(item.section)) return false;
    
    // Check if permission is granted (null permission = all can access)
    if (item.permission === null) return true;
    return rolePermissions.includes(item.permission);
  });
  
  // Group by section
  const sections = {};
  filteredItems.forEach(item => {
    if (!sections[item.section]) {
      sections[item.section] = {
        section: SECTION_CONFIG[item.section]?.name || item.section,
        order: SECTION_CONFIG[item.section]?.order || 99,
        items: [],
      };
    }
    sections[item.section].items.push(item);
  });
  
  // Convert to array and sort by order
  return Object.values(sections).sort((a, b) => a.order - b.order);
}

/**
 * Get the dashboard variant for a role
 */
export function getDashboardForRole(role) {
  switch (role) {
    case ROLES.BRAND_OWNER:
      return NAVIGATION_CONFIG.dashboards.global;
    case ROLES.CENTRAL_ADMIN:
      return NAVIGATION_CONFIG.dashboards.global;
    case ROLES.FRANCHISE_OWNER:
      return NAVIGATION_CONFIG.dashboards.franchise;
    case ROLES.REGIONAL_MANAGER:
      return NAVIGATION_CONFIG.dashboards.regional;
    default:
      return NAVIGATION_CONFIG.dashboards.outlet;
  }
}

/**
 * Get home route for a role (where to redirect after login)
 */
export function getHomeRouteForRole(role) {
  switch (role) {
    case ROLES.CHEF:
      return '/kitchen';
    case ROLES.FOH_STAFF:
      return '/pos';
    case ROLES.SUPPLIER:
      return '/supplier/orders';
    case ROLES.CUSTOMER:
      return '/order';
    default:
      return '/dashboard';
  }
}

/**
 * Check if role can perform an action
 */
export function canPerformAction(role, action) {
  const actionPermissionMap = {
    'create_order': PERMISSIONS.CREATE_ORDERS,
    'void_order': PERMISSIONS.VOID_ORDERS,
    'apply_discount': PERMISSIONS.APPLY_DISCOUNTS,
    'create_expense': PERMISSIONS.MANAGE_EXPENSES,
    'approve_expense': PERMISSIONS.APPROVE_EXPENSES,
    'create_purchase_order': PERMISSIONS.CREATE_PURCHASE_ORDERS,
    'manage_staff': PERMISSIONS.MANAGE_STAFF,
    'manage_inventory': PERMISSIONS.MANAGE_INVENTORY,
    'manage_customers': PERMISSIONS.MANAGE_CUSTOMERS,
    'manage_users': PERMISSIONS.MANAGE_USERS,
    'manage_roles': PERMISSIONS.MANAGE_ROLES,
  };
  
  const permission = actionPermissionMap[action];
  if (!permission) return false;
  return hasPermission(role, permission);
}

export default {
  ROLES,
  ROLE_DISPLAY_NAMES,
  ROLE_DESCRIPTIONS,
  ROLE_LEVELS,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  NAVIGATION_CONFIG,
  SECTION_CONFIG,
  ROLE_SECTIONS,
  ROUTE_PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  canAccessRoute,
  getNavigationForRole,
  getDashboardForRole,
  getHomeRouteForRole,
  canPerformAction,
};
